/**
 * Is Service Plans switched on for this workspace?
 *
 * 🚨 Builds its own service-role client and takes no client parameter, so no
 * call site can hand it the wrong one. A system read scoped by an explicit
 * `accountId` must never borrow a request's RLS-bound client: `auth_account_id()`
 * cannot see the `mucka_active_account` cookie and fails closed for anyone with
 * two workspace memberships, so the same query returns rows for the
 * single-membership majority and nothing for everybody else — which is exactly
 * how anti-pattern #17 hid twice, and why `logPlatformEvent` and `logEpisode`
 * both build their own.
 *
 * Cheap enough to call per request: one indexed join on a table with a handful
 * of rows per account.
 */

import { createAdminClient } from '@/lib/supabase/admin';
import { isCoreSkill } from '@/lib/skills/core';

export async function accountHasServicePlans(accountId: string): Promise<boolean> {
  return accountHasSkillSlug(accountId, 'service-plans');
}

export async function accountHasSkillSlug(accountId: string, slug: string): Promise<boolean> {
  // Quotes and Invoicing are part of every account (src/lib/skills/core.ts).
  if (isCoreSkill(slug)) return true;
  const admin = createAdminClient();
  const { data } = await admin
    .from('account_skills')
    .select('skills(slug)')
    .eq('account_id', accountId);

  return (data ?? []).some(
    r => (r.skills as unknown as { slug: string } | null)?.slug === slug,
  );
}
